import { useState, useEffect , useContext} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LanguageContext } from "../../../context/LanguageContext";
import ReCAPTCHA from "react-google-recaptcha";

function StepIndicator({ step }) {
  return (
    <div className="mt-6">
      <div className="flex items-center gap-3">
        {[1, 2, 3].map((s) => {
          const isActive = step === s;
          const isDone = step > s;

          return (
            <div key={s} className="flex items-center flex-1 gap-3">
              <div
                className={[
                  "h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold",
                  "transition-all",
                  isDone
                    ? "bg-white text-blue-700 shadow-sm"
                    : isActive
                    ? "bg-white text-blue-700 ring-2 ring-white/70"
                    : "bg-white/25 text-white",
                ].join(" ")}
              >
                {s}
              </div>

              {s < 3 && (
                <div className="relative h-1 flex-1 rounded-full bg-white/25 overflow-hidden">
                  <motion.div
                    className="absolute inset-y-0 left-0 rounded-full bg-white"
                    initial={false}
                    animate={{
                      width:
                        step <= s ? "0%" : step === s + 1 ? "100%" : "100%",
                    }}
                    transition={{ duration: 0.35 }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-white/90">
        <span className={step === 1 ? "font-semibold" : "opacity-90"}>
          Profil
        </span>
        <span className={step === 2 ? "font-semibold" : "opacity-90"}>
          Organisation
        </span>
        <span className={step === 3 ? "font-semibold" : "opacity-90"}>
          Qualité
        </span>
      </div>
    </div>
  );
}

function FieldLabel({ title, hint }) {
  return (
    <div className="md:col-span-2 mb-1">
      <h3 className="text-sm font-semibold text-gray-900 tracking-tight">
        {title}
      </h3>
      {hint ? <p className="text-xs text-gray-500 mt-1">{hint}</p> : null}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={[
        "w-full rounded-xl bg-gray-50 border border-gray-200",
        "px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400",
        "focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500",
        "transition",
        className,
      ].join(" ")}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={[
        "w-full rounded-xl bg-gray-50 border border-gray-200",
        "px-4 py-3 text-sm text-gray-900",
        "focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500",
        "transition",
        className,
      ].join(" ")}
    >
      {children}
    </select>
  );
}

function Textarea({ className = "", ...props }) {
  return (
    <textarea
      {...props}
      className={[
        "w-full rounded-xl bg-gray-50 border border-gray-200",
        "px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400",
        "focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500",
        "transition resize-none",
        className,
      ].join(" ")}
    />
  );
}

function ToggleCard({ active, onToggle, label, description }) {
  return (
    <label
      className={[
        "cursor-pointer rounded-2xl border p-4",
        "flex items-start gap-3 transition",
        active
          ? "border-blue-600 bg-blue-50"
          : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50",
      ].join(" ")}
    >
      <input
        type="checkbox"
        className="hidden"
        checked={active}
        onChange={onToggle}
      />
      <div
        className={[
          "mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center",
          active ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white",
        ].join(" ")}
      >
        {active ? <div className="h-2.5 w-2.5 rounded-sm bg-white" /> : null}
      </div>

      <div className="min-w-0">
        <div className="text-sm font-semibold text-gray-900 capitalize">
          {label}
        </div>
        {description ? (
          <div className="text-xs text-gray-500 mt-1">{description}</div>
        ) : null}
      </div>
    </label>
  );
}

/* =======================
   ADDED: Cookie + IndexedDB (client DB storage)
   ======================= */

function getCookie(name) {
  try {
    const cookieStr = typeof document !== "undefined" ? document.cookie : "";
    const cookies = cookieStr ? cookieStr.split("; ") : [];
    for (const c of cookies) {
      const [k, ...rest] = c.split("=");
      if (k === name) return decodeURIComponent(rest.join("="));
    }
    return "";
  } catch {
    return "";
  }
}

function openTeqaDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open("teqaconnect_db", 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("preinscriptions")) {
        db.createObjectStore("preinscriptions", {
          keyPath: "id",
          autoIncrement: true,
        });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function savePreinscriptionToDB(record) {
  const db = await openTeqaDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("preinscriptions", "readwrite");
    const store = tx.objectStore("preinscriptions");
    const req = store.add(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export default function TeqaConnectForm({ isOpen, onClose }) {
  const [step, setStep] = useState(1);
  const [toast, setToast] = useState(null); // ✅
  const [errors, setErrors] = useState({}); // ✅
  const { t } = useContext(LanguageContext);
  const [captchaToken, setCaptchaToken] = useState(null);


  const [form, setForm] = useState({
    // STEP 1
    nom_commercial: "",
    type_structure: "",
    ville: "",
    annee_debut: "",
    social_link: "",
    nom_responsable: "",
    telephone: "",
    whatsapp: "",
    email: "",

    // STEP 2
    nombre_agents: "",
    social_link: "",
    gestion_douteuses: "",
    capacite_jour: "",
    horaire_travail: "",
    jours_travail: "",
    experience_cod: "",
    mode_facturation: "",
    prix_commande: "",
    volume_min: "",
    services: ["confirmation"],
    langues: ["darija", "francais"],
    outils: [],
    deja_ecommerce: true,
    accepte_teqa: "",

    // STEP 3
    taux_confirmation: "",
    gestion_douteuses: "",
    reporting: "",
    multi_boutiques: false,
    accepte_conditions: false,
  });

  /* =======================
     ADDED: Validation + Errors handling
     ======================= */

  const getError = (name) => {
    const v = errors?.[name];
    if (!v) return "";
    if (Array.isArray(v)) return v[0] || "";
    if (typeof v === "string") return v;
    return "";
  };

  const hasError = (name) => Boolean(getError(name));

  const ErrorText = ({ name }) => {
    const msg = getError(name);
    return msg ? (
      <div style={{ color: "red" }} className="text-xs mt-1">
        {msg}
      </div>
    ) : null;
  };

  const asBackendErrorsShape = (map) => {
    const out = {};
    Object.keys(map || {}).forEach((k) => {
      const v = map[k];
      out[k] = Array.isArray(v) ? v : [String(v)];
    });
    return out;
  };

  const validateStep = (targetStep, data) => {
    const e = {};

    const add = (field, message) => {
      if (!e[field]) e[field] = [];
      e[field].push(message);
    };

    const isBlank = (v) =>
      v === null || v === undefined || (typeof v === "string" && v.trim() === "");

    const isInt = (v) => {
      if (isBlank(v)) return false;
      const n = Number(v);
      return Number.isInteger(n);
    };

    const isNumber = (v) => {
      if (isBlank(v)) return false;
      const n = Number(v);
      return !Number.isNaN(n);
    };

    const isBool = (v) => typeof v === "boolean";

    const isChoice = (v, choices) => !isBlank(v) && choices.includes(v);

    // STEP 1
    if (targetStep === 1) {
      if (isBlank(data.nom_commercial)) add("nom_commercial", "This field may not be blank.");
      if (!isChoice(data.type_structure, ["independant", "call_center"]))
        add("type_structure", "\"\" is not a valid choice.");
      if (isBlank(data.ville)) add("ville", "This field may not be blank.");
      if (!isInt(data.annee_debut)) add("annee_debut", "A valid integer is required.");
      if (isBlank(data.telephone)) add("telephone", "This field may not be blank.");
      if (isBlank(data.whatsapp)) add("whatsapp", "This field may not be blank.");
      if (isBlank(data.email)) add("email", "This field may not be blank.");
    }

    // STEP 2
    if (targetStep === 2) {
      if (data.type_structure === "call_center") {
        if (!isInt(data.nombre_agents)) add("nombre_agents", "A valid integer is required.");
      }
      if (!isInt(data.experience_cod)) add("experience_cod", "A valid integer is required.");
      if (!isInt(data.capacite_jour)) add("capacite_jour", "A valid integer is required.");
      if (isBlank(data.horaire_travail)) add("horaire_travail", "This field may not be blank.");
      if (!isInt(data.jours_travail)) add("jours_travail", "A valid integer is required.");
      if (!isChoice(data.accepte_teqa, ["oui", "oui_apres", "non"]))
        add("accepte_teqa", "\"\" is not a valid choice.");
      if (!isChoice(data.mode_facturation, ["commande", "forfait"]))
        add("mode_facturation", "\"\" is not a valid choice.");
      if (!isInt(data.volume_min)) add("volume_min", "A valid integer is required.");
      if (!isBool(data.deja_ecommerce)) add("deja_ecommerce", "Must be a valid boolean.");
    }

    // STEP 3
    if (targetStep === 3) {
      if (!isNumber(data.taux_confirmation))
        add("taux_confirmation", "A valid number is required.");
      if (isBlank(data.gestion_douteuses))
        add("gestion_douteuses", "This field may not be blank.");
      if (!isChoice(data.reporting, ["quotidien", "hebdomadaire"]))
        add("reporting", "\"\" is not a valid choice.");
      if (!isBool(data.multi_boutiques)) add("multi_boutiques", "Must be a valid boolean.");
      if (!isBool(data.accepte_conditions))
        add("accepte_conditions", "Must be a valid boolean.");
    }

    return asBackendErrorsShape(e);
  };

  const validateAll = (data) => {
    const e1 = validateStep(1, data);
    const e2 = validateStep(2, data);
    const e3 = validateStep(3, data);
    return { ...e1, ...e2, ...e3 };
  };

  /* Lock body scroll */
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => {
      if (!prev || !prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const toggleArray = (name, value) => {
    setForm((p) => ({
      ...p,
      [name]: p[name].includes(value)
        ? p[name].filter((v) => v !== value)
        : [...p[name], value],
    }));
    setErrors((prev) => {
      if (!prev || !prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const nextWithValidation = () => {
    const stepErrors = validateStep(step, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors((p) => ({ ...(p || {}), ...stepErrors }));
      return;
    }
    next();
  };


  const submit = async () => {

    if (!captchaToken) {
  setErrors((p) => ({
    ...p,
    captcha: ["Please verify that you are not a robot."],
  }));
   setToast({
        type: "erreur",
        message: "Capacha verification is required. Please complete the captcha.",
      });
        setTimeout(() => {
          setToast(null);
        }, 3000);
  return;
}


    const allErrors = validateAll(form);
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      return;
    } else {
      setErrors({});
    }

// ACTION
if (!form.accepte_conditions) {
  setErrors((p) => ({
    ...p,
    accepte_conditions: ["Veuillez accepter les conditions générales."],
  }));
  return;
}

setErrors({});
    const payload = {
      ...form,
      nombre_agents:
        form.type_structure === "call_center"
          ? Number(form.nombre_agents)
          : null,
      capacite_jour: Number(form.capacite_jour),
      jours_travail: Number(form.jours_travail),
      experience_cod: Number(form.experience_cod),
      prix_commande: form.prix_commande ? Number(form.prix_commande) : null,
      volume_min: form.volume_min ? Number(form.volume_min) : null,
      taux_confirmation: Number(form.taux_confirmation),
    };

    console.log("PAYLOAD DJANGO:", payload);

    /* =======================
       ADDED: Keep API call EXACTLY as provided + store to DB on success
       ======================= */

    const data = payload;

    const res = await fetch(
      "https://auth.teqaconnect.com/auth/pre-inscription/",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRFToken": getCookie("csrftoken") || "",
        },
        body: JSON.stringify(data),
      }
    );

    let apiJson = null;
    try {
      apiJson = await res.clone().json();
    } catch {
      apiJson = null;
    }

    if (res.ok) {
      await savePreinscriptionToDB({
        created_at: new Date().toISOString(),
        request_data: data,
        response_data: apiJson,
        status: "success",
      });
      // setErrors({});
      setToast({
        type: "success",
        message: "Votre inscription a été envoyée avec succès.",
      });
      setForm({
    // STEP 1
    nom_commercial: "",
    type_structure: "",
    ville: "",
    annee_debut: "",
    social_link: "",
    nom_responsable: "",
    telephone: "",
    whatsapp: "",
    email: "",

    // STEP 2
    nombre_agents: null,
    nombre_agents: null,
    experience_cod: null,
    social_link: null,
    experience_cod: null,
    gestion_douteuses: null,
    capacite_jour: "",
    horaire_travail: "",
    jours_travail: "",
    experience_cod: "",
    mode_facturation: "",
    prix_commande: "",
    volume_min: "",
    services: ["confirmation"],
    langues: ["darija", "francais"],
    outils: [],
    deja_ecommerce: true,
    accepte_teqa: "",

    // STEP 3
    taux_confirmation: "",
    gestion_douteuses: "",
    reporting: "",
    multi_boutiques: false,
    accepte_conditions: false,
  })
  setCaptchaToken(null);

    setStep(1);
    setTimeout(() => {
      setToast(null);
      onClose();
    }, 3000);

    } else {
      await savePreinscriptionToDB({
        created_at: new Date().toISOString(),
        request_data: data,
        response_data: apiJson,
        status: "failed",
        http_status: res.status,
      });
      setToast({
        type: "erreur",
        message: "erreur lors de l'envoi de votre inscription. Veuillez réessayer.",
      });
      setTimeout(() => {
        setToast(null);
      }, 3000);
      if (apiJson && typeof apiJson === "object") {
        setErrors(apiJson);
      }
      return;
    }
  };

  const overlayMotion = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const modalMotion = {
    initial: { scale: 0.96, y: 24, opacity: 0 },
    animate: { scale: 1, y: 0, opacity: 1 },
    exit: { scale: 0.96, y: 24, opacity: 0 },
    transition: { duration: 0.25, ease: "easeOut" },
  };

  const stepMotion = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { duration: 0.2 },
  };

  return (
    <AnimatePresence>
          {isOpen && (
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md px-4 py-6"
        {...overlayMotion}
      >
        {/* CLICK OUTSIDE */}
        <div className="absolute inset-0" onClick={onClose} />

        {/* MODAL */}
        <motion.div
          {...modalMotion}
          className="relative w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5"
        >
          {/* CLOSE */}
          <button
            onClick={onClose}
            className="absolute top-5 right-5 z-10 h-10 w-10 rounded-full bg-white/90 ring-1 ring-black/10 backdrop-blur
                       hover:bg-white transition flex items-center justify-center text-gray-700"
            aria-label="Fermer"
            title="Fermer"
          >
            ✕
          </button>

          {/* HEADER */}
          <div className="rounded-t-3xl bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-8 text-white">
            <div className="flex items-start justify-between gap-6">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">
                 {t("formConfirmation.title")}
                </h2>
                <p className="text-sm opacity-90 mt-1">
                  {t("formConfirmation.subtitle")}
                </p>
              </div>

              <div className="hidden md:flex items-center gap-2 rounded-2xl bg-white/10 px-4 py-2 ring-1 ring-white/15">
                <span className="text-xs text-white/80">{t("formConfirmation.step")}</span>
                <span className="text-sm font-semibold">{step}</span>
                <span className="text-xs text-white/70">/ 3</span>
              </div>
            </div>

            <StepIndicator step={step} />
          </div>

          {/* BODY */}
          <div className="max-h-[calc(92vh-220px)] overflow-y-auto">
            <div className="p-8 space-y-8">
              {/* INFO BOX */}
              <div className="rounded-2xl border border-blue-100 bg-blue-50 px-5 py-4">
                <p className="text-sm text-blue-900 font-medium">
                  {t("formConfirmation.tipTitle")} :{" "}
                  <span className="font-normal text-blue-900/90">
                    {t("formConfirmation.tipText")}
                  </span>
                </p>
              </div>

              <AnimatePresence mode="wait">
                {/* STEP 1 */}
                {step === 1 && (
                  <motion.div key="step-1" {...stepMotion}>
                    <div className="grid md:grid-cols-2 gap-4">
                      <FieldLabel
                        title="Profil & Contact"
                        hint="Informations générales sur votre structure."
                      />

                      <div>
                        <Input
                          placeholder="Nom commercial"
                          name="nom_commercial"
                          value={form.nom_commercial}
                          onChange={handleChange}
                          className={hasError("nom_commercial") ? "border-red-500" : ""}
                        />
                        <ErrorText name="nom_commercial" />
                      </div>

                      <div>
                        <Select
                          name="type_structure"
                          value={form.type_structure}
                          onChange={handleChange}
                          className={hasError("type_structure") ? "border-red-500" : ""}
                        >
                          <option value="">Type de structure</option>
                          <option value="independant">Indépendant</option>
                          <option value="call_center">Call center</option>
                        </Select>
                        <ErrorText name="type_structure" />
                      </div>

                      <div>
                        <Input
                          placeholder="Ville"
                          name="ville"
                          value={form.ville}
                          onChange={handleChange}
                          className={hasError("ville") ? "border-red-500" : ""}
                        />
                        <ErrorText name="ville" />
                      </div>

                      <div>
                        <Input
                          type="number"
                          placeholder="Année de début"
                          name="annee_debut"
                          value={form.annee_debut}
                          onChange={handleChange}
                          className={hasError("annee_debut") ? "border-red-500" : ""}
                        />
                        <ErrorText name="annee_debut" />
                      </div>

                      <div className="md:col-span-2">
                        <Input
                          className={[
                            "md:col-span-2",
                            hasError("social_link") ? "border-red-500" : "",
                          ].join(" ")}
                          placeholder="Lien social (Facebook, LinkedIn, site...)"
                          name="social_link"
                          value={form.social_link}
                          onChange={handleChange}
                        />
                        <ErrorText name="social_link" />
                      </div>

                      <div className="md:col-span-2">
                        <Input
                          className={[
                            "md:col-span-2",
                            hasError("nom_responsable") ? "border-red-500" : "",
                          ].join(" ")}
                          placeholder="Nom du responsable"
                          name="nom_responsable"
                          value={form.nom_responsable}
                          onChange={handleChange}
                        />
                        <ErrorText name="nom_responsable" />
                      </div>

                      <div>
                        <Input
                          placeholder="Téléphone"
                          name="telephone"
                          type="number"
                          value={form.telephone}
                          onChange={handleChange}
                          className={hasError("telephone") ? "border-red-500" : ""}
                        />
                        <ErrorText name="telephone" />
                      </div>

                      <div>
                        <Input
                          placeholder="WhatsApp"
                          name="whatsapp"
                          type="number"
                          value={form.whatsapp}
                          onChange={handleChange}
                          className={hasError("whatsapp") ? "border-red-500" : ""}
                        />
                        <ErrorText name="whatsapp" />
                      </div>

                      <div className="md:col-span-2">
                        <Input
                          className={[
                            "md:col-span-2",
                            hasError("email") ? "border-red-500" : "",
                          ].join(" ")}
                          placeholder="Email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                        />
                        <ErrorText name="email" />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                  <motion.div key="step-2" {...stepMotion}>
                    <div className="grid md:grid-cols-2 gap-4">
                      <FieldLabel
                        title="Organisation & Tarification"
                        hint="Capacité, horaires et services proposés."
                      />

                      {form.type_structure === "call_center" && (
                        <div>
                          <Input
                            placeholder="Nombre d'agents"
                            name="nombre_agents"
                            type="number"
                            value={form.nombre_agents}
                            onChange={handleChange}
                            className={hasError("nombre_agents") ? "border-red-500" : ""}
                          />
                          <ErrorText name="nombre_agents" />
                        </div>
                      )}

                      <div>
                        <Select
                          name="capacite_jour"
                          value={form.capacite_jour}
                          onChange={handleChange}
                          className={hasError("capacite_jour") ? "border-red-500" : ""}
                        >
                          <option value="">Commandes / jour</option>
                          <option value="50">-50</option>
                          <option value="100">50–100</option>
                          <option value="300">100–300</option>
                          <option value="500">300–500</option>
                        </Select>
                        <ErrorText name="capacite_jour" />
                      </div>

                      <div>
                        <Select
                          name="horaire_travail"
                          value={form.horaire_travail}
                          onChange={handleChange}
                          className={hasError("horaire_travail") ? "border-red-500" : ""}
                        >
                          <option value="">Horaires</option>
                          <option value="jour">Jour</option>
                          <option value="soir">Soir</option>
                          <option value="flexible">Flexible</option>
                        </Select>
                        <ErrorText name="horaire_travail" />
                      </div>

                      <div>
                        <Select
                          name="jours_travail"
                          value={form.jours_travail}
                          onChange={handleChange}
                          className={hasError("jours_travail") ? "border-red-500" : ""}
                        >
                          <option value="">Jours / semaine</option>
                          <option value="5">5 jours</option>
                          <option value="6">6 jours</option>
                          <option value="7">7 jours</option>
                        </Select>
                        <ErrorText name="jours_travail" />
                      </div>

                      <div>
                        <Input
                          placeholder="Durée d’expérience COD"
                          name="experience_cod"
                          type="number"
                          value={form.experience_cod}
                          onChange={handleChange}
                          className={hasError("experience_cod") ? "border-red-500" : ""}
                        />
                        <ErrorText name="experience_cod" />
                      </div>

                      <div>
                        <Select
                          name="mode_facturation"
                          value={form.mode_facturation}
                          onChange={handleChange}
                          className={hasError("mode_facturation") ? "border-red-500" : ""}
                        >
                          <option value="">Mode de facturation</option>
                          <option value="commande">Par commande</option>
                          <option value="forfait">Forfait</option>
                        </Select>
                        <ErrorText name="mode_facturation" />
                      </div>

                      <div>
                        <Input
                          placeholder="Prix par commande (MAD)"
                          name="prix_commande"
                          type="number"
                          value={form.prix_commande}
                          onChange={handleChange}
                          className={hasError("prix_commande") ? "border-red-500" : ""}
                        />
                        <ErrorText name="prix_commande" />
                      </div>

                      <div>
                        <Input
                          placeholder="Volume minimum / jour"
                          name="volume_min"
                          type="number"
                          value={form.volume_min}
                          onChange={handleChange}
                          className={hasError("volume_min") ? "border-red-500" : ""}
                        />
                        <ErrorText name="volume_min" />
                      </div>

                      {/* SERVICES */}
                      <div className="md:col-span-2 pt-2">
                        <p className="text-sm font-semibold text-gray-900 mb-3">
                          Services
                        </p>
                        <div className="grid sm:grid-cols-3 gap-3">
                          {["confirmation", "relance", "annulation"].map((s) => {
                            const active = form.services.includes(s);
                            return (
                              <ToggleCard
                                key={s}
                                active={active}
                                onToggle={() => toggleArray("services", s)}
                                label={s}
                              />
                            );
                          })}
                        </div>
                        <ErrorText name="services" />
                      </div>

                      {/* LANGUES */}
                      <div className="md:col-span-2 pt-2">
                        <p className="text-sm font-semibold text-gray-900 mb-3">
                          Langues
                        </p>
                        <div className="grid sm:grid-cols-4 gap-3">
                          {["darija", "francais", "arabe", "anglais"].map((l) => {
                            const active = form.langues.includes(l);
                            return (
                              <ToggleCard
                                key={l}
                                active={active}
                                onToggle={() => toggleArray("langues", l)}
                                label={l}
                              />
                            );
                          })}
                        </div>
                        <ErrorText name="langues" />
                      </div>

                      {/* OUTILS */}
                      <div className="md:col-span-2 pt-2">
                        <p className="text-sm font-semibold text-gray-900 mb-3">
                          Outils
                        </p>
                        <div className="grid sm:grid-cols-4 gap-3">
                          {["google_sheets", "crm", "excel", "autre"].map((o) => {
                            const active = form.outils.includes(o);
                            return (
                              <ToggleCard
                                key={o}
                                active={active}
                                onToggle={() => toggleArray("outils", o)}
                                label={o.replace("_", " ")}
                              />
                            );
                          })}
                        </div>
                        <ErrorText name="outils" />
                      </div>

                      {/* ECOMMERCE */}
                      <div className="md:col-span-2 pt-2">
                        <ToggleCard
                          active={form.deja_ecommerce}
                          onToggle={() =>
                            setForm((p) => ({
                              ...p,
                              deja_ecommerce: !p.deja_ecommerce,
                            }))
                          }
                          label="Déjà travaillé avec des e-commerces"
                          description="Indiquez si vous avez déjà géré des commandes e-commerce."
                        />
                        <ErrorText name="deja_ecommerce" />
                      </div>

                      <div className="md:col-span-2">
                        <Select
                          className={[
                            "md:col-span-2",
                            hasError("accepte_teqa") ? "border-red-500" : "",
                          ].join(" ")}
                          name="accepte_teqa"
                          value={form.accepte_teqa}
                          onChange={handleChange}
                        >
                          <option value="">Accepte TEQA</option>
                          <option value="oui">Oui</option>
                          <option value="oui_apres">Oui après explication</option>
                          <option value="non">Non</option>
                        </Select>
                        <ErrorText name="accepte_teqa" />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* STEP 3 */}
                {step === 3 && (
                  <motion.div key="step-3" {...stepMotion}>
                    <div className="grid md:grid-cols-2 gap-4">
                      <FieldLabel
                        title="Qualité & Process"
                        hint="Comment vous gérez la performance et le reporting."
                      />

                      <div className="md:col-span-2">
                        <Input
                          className={[
                            "md:col-span-2",
                            hasError("taux_confirmation") ? "border-red-500" : "",
                          ].join(" ")}
                          placeholder="Taux de confirmation (%)"
                          name="taux_confirmation"
                          type="number"
                          value={form.taux_confirmation}
                          onChange={handleChange}
                        />
                        <ErrorText name="taux_confirmation" />
                      </div>

                      <div className="md:col-span-2">
                        <Textarea
                          className={[
                            "md:col-span-2 h-32",
                            hasError("gestion_douteuses") ? "border-red-500" : "",
                          ].join(" ")}
                          placeholder="Gestion des commandes douteuses"
                          name="gestion_douteuses"
                          value={form.gestion_douteuses}
                          onChange={handleChange}
                        />
                        <ErrorText name="gestion_douteuses" />
                      </div>

                      <div className="md:col-span-2">
                        <Select
                          className={[
                            "md:col-span-2",
                            hasError("reporting") ? "border-red-500" : "",
                          ].join(" ")}
                          name="reporting"
                          value={form.reporting}
                          onChange={handleChange}
                        >
                          <option value="">Reporting</option>
                          <option value="quotidien">Quotidien</option>
                          <option value="hebdomadaire">Hebdomadaire</option>
                        </Select>
                        <ErrorText name="reporting" />
                      </div>

                      <div className="md:col-span-2 grid md:grid-cols-2 gap-3 pt-2">
                        <div>
                          <ToggleCard
                            active={form.multi_boutiques}
                            onToggle={() =>
                              setForm((p) => ({
                                ...p,
                                multi_boutiques: !p.multi_boutiques,
                              }))
                            }
                            label="Multi-boutiques"
                            description="Je peux gérer plusieurs boutiques."
                          />
                          <ErrorText name="multi_boutiques" />
                        </div>

                        <div>
                          <ToggleCard
                            active={form.accepte_conditions}
                            onToggle={() =>
                              setForm((p) => ({
                                ...p,
                                accepte_conditions: !p.accepte_conditions,
                              }))
                            }
                            label="Conditions générales"
                            description="J’accepte les conditions générales."
                          />
                          <ErrorText name="accepte_conditions" />
                        </div>
                      </div>

                      {/* SUCCESS/INFO BOX */}
                      <div className="md:col-span-2 mt-2">
                        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4">
                          <p className="text-sm text-emerald-900 font-medium">
                            Presque fini ✨{" "}
                            <span className="font-normal text-emerald-900/90">
                              Vérifiez vos informations, puis envoyez la
                              demande.
                            </span>
                          </p>
                        </div>
                      </div>
                      {/* CAPTCHA */}
                      <div className="flex justify-center">
                        <ReCAPTCHA
                          sitekey="6Lc-eVwsAAAAAHTRJLIV8adlg8mffSxEXA0SdyKW"
                          onChange={(token) => setCaptchaToken(token)}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* ACTIONS */}
              <div className="flex justify-between items-center pt-8 border-t border-gray-100">
                <button
                  onClick={prev}
                  disabled={step === 1}
                  className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700
                             hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed
                             transition"
                >
                  {t("formConfirmation.back")}
                </button>

                {step < 3 ? (
                  <button
                    onClick={nextWithValidation}
                    className="px-6 py-3 rounded-xl text-white font-medium
                               bg-gradient-to-r from-blue-600 to-indigo-600
                               hover:opacity-95 active:scale-[0.99] transition"
                  >
                    {t("formConfirmation.continue")}
                  </button>
                ) : (
                  <button
                    onClick={submit}
                    className="px-6 py-3 rounded-xl text-white font-medium
                               bg-gradient-to-r from-emerald-600 to-green-600
                               hover:opacity-95 active:scale-[0.99] transition"
                  >
                    {t("formConfirmation.send")}
                  </button>
                )}
              </div>

              {/* FOOTER NOTE */}
              <div className="text-xs text-gray-500 flex items-center justify-between">
                <span> {t("formConfirmation.footerLeft")}</span>
                <span className="hidden sm:inline">
                  {t("formConfirmation.footerRight")}
                </span>
              </div>
            </div>
          </div>
{/* TOAST – TOP RIGHT */}
{toast && (
  toast.type === "erreur" ? (
    <div
      className="
        fixed top-6 right-6 z-[9999]
        rounded-xl px-5 py-3 text-sm font-medium
        shadow-lg border
        bg-red-50 text-red-800 border-red-200
        animate-fade-in
      "
    >
      {toast.message}
    </div>
  ) : (
    <div
      className="
        fixed top-6 right-6 z-[9999]
        rounded-xl px-5 py-3 text-sm font-medium
        shadow-lg border
        bg-emerald-50 text-emerald-800 border-emerald-200
        animate-fade-in
      "
    >
      {toast.message}
    </div>
  )
)}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
}
